import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  NormalizedVideoStats,
  SentimentOverlayData,
  VideoSectionStatsMap,
} from './sentimentTypes';

interface UseSentimentDataParams {
  activeVideoId: string | null;
  videoDuration: number;
  videoSectionId: string | null;
  videoSectionStats: VideoSectionStatsMap;
}

interface UseSentimentDataResult {
  addOptimisticSentiment: (sentimentTime: number) => void;
  hasSentimentData: boolean;
  overlay: SentimentOverlayData | null;
  videoStats: NormalizedVideoStats | null;
}

function getOptimisticKey(videoSectionId: string | null, videoId: string | null) {
  return videoSectionId && videoId ? `${videoSectionId}:${videoId}` : null;
}

export function useSentimentData({
  activeVideoId,
  videoDuration,
  videoSectionId,
  videoSectionStats,
}: UseSentimentDataParams): UseSentimentDataResult {
  const [optimisticTimesByKey, setOptimisticTimesByKey] = useState<
    Record<string, number[]>
  >({});
  const optimisticKey = getOptimisticKey(videoSectionId, activeVideoId);
  const videoStats =
    videoSectionId && activeVideoId
      ? (videoSectionStats[videoSectionId]?.videos[activeVideoId] ?? null)
      : null;

  useEffect(() => {
    setOptimisticTimesByKey({});
  }, [videoSectionStats]);

  const addOptimisticSentiment = useCallback(
    (sentimentTime: number) => {
      if (!optimisticKey || sentimentTime < 0) {
        return;
      }

      setOptimisticTimesByKey((current) => ({
        ...current,
        [optimisticKey]: [...(current[optimisticKey] ?? []), sentimentTime],
      }));
    },
    [optimisticKey],
  );

  const overlay = useMemo(() => {
    if (!videoStats || videoStats.intervalLength <= 0) {
      return null;
    }

    const intervals = [...videoStats.normalizedSentiment];
    const fallbackDuration =
      videoStats.intervalLength *
      Math.max(videoStats.normalizedSentiment.length, intervals.length, 1);
    const overlayDuration = videoDuration > 0 ? videoDuration : fallbackDuration;

    if (overlayDuration <= 0) {
      return null;
    }

    const optimisticTimes = optimisticKey
      ? (optimisticTimesByKey[optimisticKey] ?? [])
      : [];

    optimisticTimes.forEach((sentimentTime) => {
      const intervalIndex = Math.floor(sentimentTime / videoStats.intervalLength);

      if (intervalIndex < 0) {
        return;
      }

      while (intervals.length <= intervalIndex) {
        intervals.push(0);
      }

      intervals[intervalIndex] = Math.max(intervals[intervalIndex] ?? 0, 1);
    });

    return {
      intervalLength: videoStats.intervalLength,
      intervals,
      videoDuration: overlayDuration,
    };
  }, [optimisticKey, optimisticTimesByKey, videoDuration, videoStats]);

  return {
    addOptimisticSentiment,
    hasSentimentData: Boolean(overlay?.intervals.some((value) => value > 0)),
    overlay,
    videoStats,
  };
}
